// Pricing page inspired by Photo AI design, adapted for Mapas KM
import { createElement } from '@/utils/dom'
import { router } from '@/utils/router'
import { Footer } from '@/components/footer'
import { stripeService } from '@/services/stripe'
import { authService } from '@/services/auth'
import { subscriptionService } from '@/services/subscription'

interface PricingTier {
  name: string
  price: number
  originalPrice?: number  // For strikethrough effect
  subtitle: string
  features: string[]
  available: boolean
  popular?: boolean
  highlighted?: boolean
}

export class PricingPage {
  private soloPrice: number = 29.99
  private empresasPrice: number = 199.00
  private ultraPremiumPrice: number = 499.00

  private pricingData: PricingTier[] = [
    {
      name: 'Solo',
      price: 29.99, // Will be updated from API
      originalPrice: 44.99, // Strikethrough price
      subtitle: '29,99€ por mês<br>1 colaborador', // Will be updated
      features: [
        'Simulações de rota para 1 colaborador',
        'Estrutura de dados profissional',
        'Exportação para Excel',
        'Armazenamento de 12 meses',
        'Validação automática de NIF',
        'Cálculo automático de distâncias'
      ],
      available: true
    },
    {
      name: 'Empresas',
      price: 199.00,
      originalPrice: 299.00, // Strikethrough price
      subtitle: '199,00€ por mês<br>Até 6 colaboradores',
      features: [
        'Todas as funcionalidades do Solo',
        'Simulações para até 6 colaboradores',
        'Gestão de múltiplos colaboradores',
        'Exportação ZIP com relatórios individuais',
        'Suporte prioritário',
        'Relatórios mensais automatizados'
      ],
      available: true
    },
    {
      name: 'Ultra Premium',
      price: 499.00,
      originalPrice: 749.00, // Strikethrough price
      subtitle: '499,00€ por mês<br>Até 60 colaboradores',
      features: [
        'Todas as funcionalidades anteriores',
        'Simulações para até 60 colaboradores',
        'Gestão de múltiplos colaboradores',
        'Exportação ZIP com relatórios individuais',
        'Suporte prioritário',
        'Relatórios mensais automatizados'
      ],
      available: true
    }
  ]

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'pricing-page' })

    // Load pricing from API
    await this.loadPricingFromAPI()

    // Navigation
    page.appendChild(this.createNavigation())

    // Header
    page.appendChild(this.createHeader())

    // Pricing cards
    page.appendChild(this.createPricingGrid())

    // Features comparison (optional)
    page.appendChild(this.createFeaturesComparison())

    // Footer
    const footer = new Footer()
    page.appendChild(footer.render())

    // Check for subscription intent after authentication
    this.handleSubscriptionIntent()

    return page
  }

  private async handleSubscriptionIntent(): Promise<void> {
    const intentData = localStorage.getItem('subscription_intent')
    if (!intentData) return

    const user = authService.getUser()
    if (!user) return // User not logged in yet

    try {
      const intent = JSON.parse(intentData)

      // Check if intent is recent (within last 30 minutes)
      const now = Date.now()
      const thirtyMinutes = 30 * 60 * 1000
      if (now - intent.timestamp > thirtyMinutes) {
        localStorage.removeItem('subscription_intent')
        return
      }

      // Clear the intent
      localStorage.removeItem('subscription_intent')

      // Proceed with checkout
      // Convert tier name to lowercase and replace spaces with underscores for backend compatibility
      // e.g., "Ultra Premium" -> "ultra_premium"
      const backendTierName = intent.tierName.toLowerCase().replace(/\s+/g, '_')
      await stripeService.redirectToCheckout(backendTierName)

    } catch (error: any) {
      console.error('Failed to process subscription intent:', error)
      localStorage.removeItem('subscription_intent')

      // Show the specific error message from API
      const msg = error?.message ?? 'Falha ao iniciar processo de pagamento. Tente novamente.'
      alert(msg)

      // Special handling for missing email
      if (error?.code === 'user_email_required') {
        setTimeout(() => {
          alert('Você será redirecionado para atualizar o seu perfil com um email válido.')
        }, 1000)
      }
    }
  }

  private createNavigation(): HTMLElement {
    const nav = createElement('nav', { className: 'nav' })

    nav.innerHTML = `
      <div class="container">
        <div class="nav-container">
          <div class="nav-left">
            <a href="#" class="nav-logo">
              🗺️ Mapas KM
            </a>
            <ul class="nav-menu">
              <li><a href="/" class="nav-link">Início</a></li>
              <li><a href="/pricing" class="nav-link active">Preços</a></li>
              <li><a href="/funcionalidades" class="nav-link">Funcionalidades</a></li>
            </ul>
          </div>
          <div class="nav-right">
            <a href="/register" class="nav-cta">Começar Grátis →</a>
            <a href="/login" class="nav-login">Login</a>
          </div>
          <button class="mobile-menu-toggle" aria-label="Menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        
        <!-- Mobile Menu -->
        <div class="mobile-menu">
          <div class="mobile-menu-header">
            <div class="mobile-menu-logo">🗺️ Mapas KM</div>
            <button class="mobile-menu-close" aria-label="Fechar menu">✕</button>
          </div>
          <div class="mobile-menu-content">
            <div class="mobile-menu-nav">
              <a href="/" class="mobile-nav-link">Início</a>
              <a href="/pricing" class="mobile-nav-link active">Preços</a>
              <a href="/funcionalidades" class="mobile-nav-link">Funcionalidades</a>
            </div>
            <div class="mobile-menu-actions">
              <a href="/register" class="mobile-nav-cta">Começar Grátis →</a>
              <a href="/login" class="mobile-nav-login">Login</a>
            </div>
          </div>
        </div>
      </div>
    `

    // Add click handlers
    nav.addEventListener('click', (e) => {
      const target = e.target as HTMLElement

      // Handle mobile menu toggle
      if (target.closest('.mobile-menu-toggle')) {
        e.preventDefault()
        const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
        const isOpen = mobileMenu.classList.contains('open')

        if (isOpen) {
          mobileMenu.classList.remove('open')
          document.body.classList.remove('mobile-menu-open')
        } else {
          mobileMenu.classList.add('open')
          document.body.classList.add('mobile-menu-open')
        }
        return
      }

      // Handle mobile menu close button
      if (target.closest('.mobile-menu-close')) {
        e.preventDefault()
        const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
        mobileMenu.classList.remove('open')
        document.body.classList.remove('mobile-menu-open')
        return
      }

      // Handle navigation links
      if (target.tagName === 'A') {
        e.preventDefault()
        const href = target.getAttribute('href')

        // Close mobile menu if open
        const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
        mobileMenu.classList.remove('open')
        document.body.classList.remove('mobile-menu-open')

        if (href === '/' || href === '/login' || href === '/register' || href === '/dashboard' || href === '/funcionalidades') {
          router.navigate(href)
        } else if (href === '#' && target.classList.contains('nav-logo')) {
          router.navigate('/')
        }
      }
    })

    return nav
  }

  private createHeader(): HTMLElement {
    const header = createElement('section', { className: 'pricing-header' })

    const user = authService.getUser()
    const currentSubscription = user ? this.getCurrentSubscriptionInfo() : null

    header.innerHTML = `
      <div class="container">
        <div class="text-center">
          <h1 style="font-size: 3rem; font-weight: bold; margin-bottom: 1rem; color: white;">
            Preços
          </h1>
          <p style="font-size: 1.25rem; color: var(--text-secondary); max-width: 600px; margin: 0 auto;">
          Escolha o plano perfeito para automatizar a simulação de rotas da sua empresa portuguesa
          </p>
          ${currentSubscription ? `
            <div class="current-subscription-info" style="margin-top: 1.5rem;">
              <div class="subscription-card">
                <span class="current-badge">Plano Atual</span>
                <span class="subscription-name">${currentSubscription.name}</span>
                <span class="subscription-status" style="color: ${currentSubscription.color};">
                  ${currentSubscription.status}
                </span>
                ${currentSubscription.expirationInfo ? `
                  <span class="subscription-expiry">${currentSubscription.expirationInfo}</span>
                ` : ''}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `

    return header
  }

  private getCurrentSubscriptionInfo(): { name: string; status: string; color: string; expirationInfo?: string } | null {
    const user = authService.getUser()
    if (!user) return null

    const status = user.subscription_status
    const statusText = authService.getSubscriptionDisplayStatus()
    const color = subscriptionService.getSubscriptionStatusColor(status)

    let planName = 'Conta Gratuita'
    if (user.plan_id === 'solo' || user.plan_id === 'iniciante') {
      planName = 'Plano Solo'
    } else if (user.plan_id === 'empresas') {
      planName = 'Plano Empresas'
    } else if (user.plan_id === 'premium' || user.plan_id === 'ultra_premium') {
      planName = 'Plano Ultra Premium'
    }

    let expirationInfo = undefined
    if (user.subscription_end_date && status === 'active') {
      const daysUntil = subscriptionService.getDaysUntilExpiration()
      if (daysUntil !== null) {
        expirationInfo = `Expira em ${daysUntil} dias`
      }
    }

    return {
      name: planName,
      status: statusText,
      color,
      expirationInfo
    }
  }



  private createPricingGrid(): HTMLElement {
    const grid = createElement('section', { className: 'pricing-grid-section' })

    grid.innerHTML = `
      <div class="container">
        <div class="pricing-grid" id="pricing-grid">
          ${this.pricingData.map(tier => this.createPricingCard(tier)).join('')}
        </div>
      </div>
    `

    // Add click handlers for subscribe buttons
    grid.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('subscribe-btn')) {
        e.preventDefault()
        const tierName = target.getAttribute('data-tier')
        await this.handleSubscribe(tierName!, target as HTMLButtonElement)
      }
    })

    return grid
  }

  private createPricingCard(tier: PricingTier): string {
    const priceText = this.formatPrice(tier.price)
    const hasDiscount = tier.originalPrice && tier.originalPrice > tier.price
    const originalPriceText = hasDiscount ? this.formatPrice(tier.originalPrice!) : ''

    return `
      <div class="pricing-card ${tier.popular ? 'popular' : ''} ${!tier.available ? 'disabled' : ''}">
        ${tier.popular ? '<div class="popular-badge">Mais popular</div>' : ''}
        
        <div class="card-header">
          <h3 class="tier-name">${tier.name}</h3>
          <div class="price-container">
            ${hasDiscount ? `
              <div style="display: flex; flex-direction: column; align-items: center; gap: 0.25rem;">
                <span class="price-original" style="text-decoration: line-through; color: var(--text-muted); font-size: 1.25rem; font-weight: 400;">${originalPriceText}</span>
                <span class="price">${priceText}</span>
              </div>
            ` : `
              <span class="price">${priceText}</span>
            `}
            <span class="period">por mês</span>
          </div>
          <div class="tier-subtitle">${tier.subtitle}</div>
        </div>

        <div class="card-body">
          <button class="subscribe-btn ${tier.available ? 'available' : 'disabled'}" 
                  data-tier="${tier.name}" 
                  ${!tier.available ? 'disabled' : ''}>
            ${tier.available ? 'Subscrever Agora →' : 'Em Construção'}
          </button>
          
          ${tier.available ? `
            <div class="view-billing">
              <a href="#" class="billing-link">Ver faturação mensal ↗</a>
            </div>
          ` : ''}

          <ul class="features-list">
            ${tier.features.map(feature => `
              <li class="feature-item">
                <span class="feature-icon">${this.getFeatureIcon(feature)}</span>
                ${this.formatFeature(feature)}
              </li>
            `).join('')}
          </ul>

          ${!tier.available ? `
            <div class="coming-soon">
              <div class="features-preview">
                <span class="preview-text">← Todas as funcionalidades ${tier.name === 'Ultra Premium' ? 'anteriores' : tier.name}, mais:</span>
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `
  }

  private getFeatureIcon(feature: string): string {
    if (feature.includes('📄') || feature.includes('✏️') || feature.includes('✂️')) {
      return '✅'
    }
    return '✅'
  }

  private formatFeature(feature: string): string {
    // Handle special formatting for certain features
    if (feature.includes('qualidade')) {
      const quality = feature.includes('baixa') ? 'baixa' :
        feature.includes('média') ? 'média' :
          feature.includes('alta') ? 'alta' : 'ultra'

      const colorClass = quality === 'baixa' ? 'feature-low' :
        quality === 'média' ? 'feature-medium' :
          quality === 'alta' ? 'feature-high' : 'feature-ultra'

      return feature.replace(quality, `<span class="${colorClass}">${quality}</span>`)
    }

    return feature
  }



  private createFeaturesComparison(): HTMLElement {
    const section = createElement('section', { className: 'features-comparison' })

    section.innerHTML = `
      <div class="container">
        <div class="text-center mb-8">
          <h2>Escolha o plano que se adequa às necessidades da sua empresa</h2>
          <p>Todos os planos incluem estrutura de dados profissional e exportação organizada para Excel</p>
        </div>
        
        <div style="margin-top: 4rem; padding: 3rem; background: var(--gradient-card); border-radius: 1rem; text-align: center; border: 1px solid var(--border-color);">
          <h3>Pronto para automatizar a simulação das suas rotas empresariais?</h3>
          <p style="margin-bottom: 2rem;">Comece com o nosso plano Solo e evolua à medida que a sua empresa cresce.</p>
          
          <div class="bottom-signup-form" style="display: grid; grid-template-columns: 1fr auto; gap: 1rem; max-width: 500px; margin: 0 auto;">
            <input 
              type="text" 
              placeholder="Digite o seu NIF..." 
              class="form-input" 
              style="border-radius: 0.75rem; padding: 1.25rem;"
              id="bottom-nif"
              maxlength="9"
              pattern="[0-9]{9}"
            >
            <button class="btn btn-success btn-lg" id="bottom-cta">
              Começar →
            </button>
          </div>
          
          <style>
            @media (max-width: 768px) {
              .bottom-signup-form {
                grid-template-columns: 1fr !important;
                gap: 1rem !important;
              }
            }
          </style>
          
          <div class="form-error" id="bottom-nif-error" style="display: none; margin-top: 1rem;"></div>
          
          <div style="margin-top: 1rem; color: var(--text-muted); font-size: 0.875rem;">
            Se já tem uma conta, iremos fazer o login automaticamente
          </div>
        </div>
      </div>
    `

    // Add bottom NIF capture functionality
    const nifInput = section.querySelector('#bottom-nif') as HTMLInputElement
    const errorDiv = section.querySelector('#bottom-nif-error') as HTMLElement
    const ctaButton = section.querySelector('#bottom-cta') as HTMLButtonElement

    // Add input formatting for NIF (only numbers)
    nifInput.addEventListener('input', (e) => {
      const input = e.target as HTMLInputElement
      input.value = input.value.replace(/\D/g, '') // Only numbers
    })

    ctaButton.addEventListener('click', async (e) => {
      e.preventDefault()
      await this.handleNifSubmit(nifInput, errorDiv, ctaButton)
    })

    nifInput.addEventListener('keypress', async (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        await this.handleNifSubmit(nifInput, errorDiv, ctaButton)
      }
    })

    return section
  }

  private async loadPricingFromAPI(): Promise<void> {
    try {
      const response = await fetch('/api/stripe/config')
      if (response.ok) {
        const config = await response.json()

        // Update Solo plan
        if (config.prices?.solo || config.prices?.iniciante) {
          this.soloPrice = config.prices.solo || config.prices.iniciante
          const soloMaxColab = config.plans?.solo?.maxColaboradores ?? 1

          const soloTier = this.pricingData.find(t => t.name === 'Solo')
          if (soloTier) {
            soloTier.price = this.soloPrice
            soloTier.originalPrice = Math.round(this.soloPrice * 1.5 * 100) / 100 // Calculate strikethrough
            soloTier.subtitle = `${this.formatPrice(this.soloPrice)} por mês<br>${soloMaxColab} colaborador`
          }
        }

        // Update Empresas plan
        if (config.prices?.empresas) {
          this.empresasPrice = config.prices.empresas
          const empresasMaxColab = config.plans?.empresas?.maxColaboradores ?? 6

          const empresasTier = this.pricingData.find(t => t.name === 'Empresas')
          if (empresasTier) {
            empresasTier.price = this.empresasPrice
            empresasTier.originalPrice = Math.round(this.empresasPrice * 1.5 * 100) / 100 // Calculate strikethrough
            empresasTier.subtitle = `${this.formatPrice(this.empresasPrice)} por mês<br>Até ${empresasMaxColab} colaboradores`
          }
        }

        // Update Ultra Premium plan
        if (config.prices?.ultra_premium) {
          this.ultraPremiumPrice = config.prices.ultra_premium
          const ultraMaxColab = config.plans?.ultra_premium?.maxColaboradores ?? 60

          const ultraTier = this.pricingData.find(t => t.name === 'Ultra Premium')
          if (ultraTier) {
            ultraTier.price = this.ultraPremiumPrice
            ultraTier.originalPrice = Math.round(this.ultraPremiumPrice * 1.5 * 100) / 100 // Calculate strikethrough
            ultraTier.subtitle = `${this.formatPrice(this.ultraPremiumPrice)} por mês<br>Até ${ultraMaxColab} colaboradores`
          }
        }
      }
    } catch (error) {
      console.error('Failed to load pricing from API:', error)
      // Continue with default prices
    }
  }

  private formatPrice(price: number): string {
    return `${price.toFixed(2).replace('.', ',')}€`
  }

  private async handleSubscribe(tierName: string, button: HTMLButtonElement): Promise<void> {
    const tier = this.pricingData.find(t => t.name === tierName)

    if (!tier?.available) {
      return
    }

    // Check if user is authenticated
    const user = authService.getUser()
    if (!user) {
      // Store the subscription intent for after login/registration
      localStorage.setItem('subscription_intent', JSON.stringify({
        tierName,
        timestamp: Date.now()
      }))

      // Redirect to registration with return URL
      router.navigate('/register?return=pricing&action=subscribe')
      return
    }

    // Show loading state
    const originalText = button.textContent
    button.textContent = 'A processar...'
    button.disabled = true

    try {
      // Redirect to Stripe checkout
      // Convert tier name to lowercase and replace spaces with underscores for backend compatibility
      // e.g., "Ultra Premium" -> "ultra_premium"
      const backendTierName = tierName.toLowerCase().replace(/\s+/g, '_')
      await stripeService.redirectToCheckout(backendTierName)

    } catch (error: any) {
      console.error('Failed to process subscription:', error)

      // Show the specific error message from API
      const msg = error?.message ?? 'Falha ao iniciar processo de pagamento. Tente novamente.'
      alert(msg)

      // Special handling for missing email
      if (error?.code === 'user_email_required') {
        // Small delay so the alert is read, then redirect to profile
        setTimeout(() => {
          // For now just show additional guidance
          alert('Você será redirecionado para atualizar o seu perfil com um email válido.')
        }, 1000)
      }

      // Restore button
      button.textContent = originalText
      button.disabled = false
    }
  }

  private async handleNifSubmit(
    nifInput: HTMLInputElement,
    errorDiv: HTMLElement,
    button: HTMLButtonElement
  ): Promise<void> {
    const nif = nifInput.value.trim()

    // Hide previous errors
    errorDiv.style.display = 'none'

    // Show loading state
    const originalText = button.textContent
    button.textContent = 'A processar...'
    button.disabled = true

    try {
      // Navigate to register page with NIF parameter if provided
      if (nif) {
        router.navigate(`/register?nif=${encodeURIComponent(nif)}`)
      } else {
        router.navigate('/register')
      }

    } catch (error) {
      console.error('Failed to navigate:', error)
      this.showError(errorDiv, 'Erro ao navegar. Tente novamente.')

      // Restore button
      button.textContent = originalText
      button.disabled = false
    }
  }

  private showError(errorDiv: HTMLElement, message: string): void {
    errorDiv.textContent = message
    errorDiv.style.display = 'block'
  }
}
